import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Type for storage location options.
 * - 'localStorage': Browser localStorage (persists across sessions)
 * - 'sessionStorage': Browser sessionStorage (cleared when tab closes)
 * - 'both': Saves to both storages for synchronization
 */
export type StorageType = 'localStorage' | 'sessionStorage' | 'both';

/**
 * Centralized storage service for managing localStorage and sessionStorage operations.
 * Provides reactive storage changes via Observable and cross-tab synchronization.
 *
 * @remarks
 * This service abstracts all browser storage operations and provides:
 * - Type-safe storage operations
 * - Reactive storage change notifications
 * - Cross-tab synchronization
 * - JSON serialization/deserialization
 * - Error handling for storage quota and parsing errors
 *
 * @example
 * ```typescript
 * // Get item
 * const tenant = storageService.getItem<Tenant>('tenant');
 *
 * // Set item
 * storageService.setItem('user', userData);
 *
 * // Observe changes
 * storageService.observeKey('tenant').subscribe(tenant => {
 *   console.log('Tenant changed:', tenant);
 * });
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private storageChangeSubject = new BehaviorSubject<{ key: string; value: unknown } | null>(null);

  /**
   * Observable that emits whenever storage changes occur.
   * Emits an object with the changed key and new value, or null for clear operations.
   */
  public storageChange$: Observable<{ key: string; value: unknown } | null> =
    this.storageChangeSubject.asObservable();

  constructor() {
    // Listen for storage changes from other tabs/windows
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (event) => {
        if (event.key) {
          this.notifyChange(event.key, event.newValue);
        }
      });
    }
  }

  /**
   * Retrieves a value from storage.
   *
   * @param key - The storage key to retrieve
   * @param storageType - The storage location ('localStorage', 'sessionStorage', or 'both')
   * @returns The parsed value, or null if not found or if parsing fails
   *
   * @example
   * ```typescript
   * const user = storageService.getItem<User>('user');
   * const token = storageService.getItem<string>('token', 'sessionStorage');
   * ```
   */
  getItem<T = string>(key: string, storageType: StorageType = 'localStorage'): T | null {
    try {
      if (storageType === 'sessionStorage') {
        const value = sessionStorage.getItem(key);
        return value ? (JSON.parse(value) as T) : null;
      } else if (storageType === 'both') {
        // Try localStorage first, fallback to sessionStorage
        const localValue = localStorage.getItem(key);
        if (localValue) {
          return JSON.parse(localValue) as T;
        }
        const sessionValue = sessionStorage.getItem(key);
        return sessionValue ? (JSON.parse(sessionValue) as T) : null;
      } else {
        const value = localStorage.getItem(key);
        return value ? (JSON.parse(value) as T) : null;
      }
    } catch {
      return null;
    }
  }

  /**
   * Stores a value in storage with automatic JSON serialization.
   *
   * @param key - The storage key
   * @param value - The value to store (will be JSON stringified)
   * @param storageType - The storage location ('localStorage', 'sessionStorage', or 'both')
   *
   * @example
   * ```typescript
   * storageService.setItem('user', userObject);
   * storageService.setItem('token', 'abc123', 'sessionStorage');
   * storageService.setItem('settings', settings, 'both'); // Saves to both storages
   * ```
   */
  setItem<T = unknown>(key: string, value: T, storageType: StorageType = 'localStorage'): void {
    try {
      const stringValue = JSON.stringify(value);

      if (storageType === 'sessionStorage') {
        sessionStorage.setItem(key, stringValue);
      } else if (storageType === 'both') {
        // Save to both storages
        localStorage.setItem(key, stringValue);
        sessionStorage.setItem(key, stringValue);
      } else {
        localStorage.setItem(key, stringValue);
      }

      this.notifyChange(key, value);
    } catch {
      // Storage quota exceeded or other error
    }
  }

  /**
   * Removes a value from storage.
   *
   * @param key - The storage key to remove
   * @param storageType - The storage location ('localStorage', 'sessionStorage', or 'both')
   */
  removeItem(key: string, storageType: StorageType = 'localStorage'): void {
    if (storageType === 'sessionStorage') {
      sessionStorage.removeItem(key);
    } else if (storageType === 'both') {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } else {
      localStorage.removeItem(key);
    }

    this.notifyChange(key, null);
  }

  /**
   * Clears all items from the specified storage.
   * Note: This clears ALL items, not just those managed by this service.
   *
   * @param storageType - The storage location to clear ('localStorage', 'sessionStorage', or 'both')
   */
  clear(storageType: StorageType = 'localStorage'): void {
    if (storageType === 'sessionStorage') {
      sessionStorage.clear();
    } else if (storageType === 'both') {
      localStorage.clear();
      sessionStorage.clear();
    } else {
      localStorage.clear();
    }

    this.notifyChange('*', null);
  }

  /**
   * Retrieves multiple items from storage in a single operation.
   *
   * @param keys - Array of storage keys to retrieve
   * @param storageType - The storage location
   * @returns Object with keys as properties and their values (or null if not found)
   *
   * @example
   * ```typescript
   * const items = storageService.getItems(['user', 'tenant', 'settings']);
   * console.log(items.user); // User object or null
   * console.log(items.tenant); // Tenant object or null
   * ```
   */
  getItems<T = unknown>(
    keys: string[],
    storageType: StorageType = 'localStorage'
  ): Record<string, T | null> {
    const result: Record<string, T | null> = {};
    keys.forEach((key) => {
      result[key] = this.getItem<T>(key, storageType);
    });
    return result;
  }

  /**
   * Stores multiple items in a single operation.
   *
   * @param items - Object with key-value pairs to store
   * @param storageType - The storage location
   *
   * @example
   * ```typescript
   * storageService.setItems({
   *   user: userObject,
   *   tenant: tenantObject,
   *   settings: settingsObject
   * });
   * ```
   */
  setItems<T = unknown>(items: Record<string, T>, storageType: StorageType = 'localStorage'): void {
    Object.keys(items).forEach((key) => {
      this.setItem(key, items[key], storageType);
    });
  }

  /**
   * Removes multiple items from storage in a single operation.
   *
   * @param keys - Array of storage keys to remove
   * @param storageType - The storage location
   */
  removeItems(keys: string[], storageType: StorageType = 'localStorage'): void {
    keys.forEach((key) => {
      this.removeItem(key, storageType);
    });
  }

  /**
   * Internal method to notify subscribers of storage changes.
   *
   * @param key - The changed storage key
   * @param value - The new value (null for removals)
   */
  private notifyChange(key: string, value: unknown): void {
    this.storageChangeSubject.next({ key, value });
  }

  /**
   * Creates an Observable that emits whenever a specific storage key changes.
   * Emits the current value immediately upon subscription, then emits on every change.
   *
   * @param key - The storage key to observe
   * @returns Observable that emits the value whenever it changes
   *
   * @example
   * ```typescript
   * storageService.observeKey<Tenant>('tenant').subscribe(tenant => {
   *   if (tenant) {
   *     console.log('Tenant selected:', tenant.name);
   *   }
   * });
   * ```
   */
  observeKey<T = unknown>(key: string): Observable<T | null> {
    return new Observable((observer) => {
      // Get initial value
      observer.next(this.getItem<T>(key));

      // Subscribe to changes
      const subscription = this.storageChange$.subscribe((change) => {
        if (change && (change.key === key || change.key === '*')) {
          observer.next(this.getItem<T>(key));
        }
      });

      return () => subscription.unsubscribe();
    });
  }
}
